import React, { useContext } from 'react';

import {
  AppContext,
  formatCurrency,
  INTL_UNSUPPORTED_CURRENCIES
} from '@nile/components';

import { CurrencyField as DesignSystemCurrencyField } from '@superops/ui-design-system';
import * as currencyFormatterPkg from 'currency-formatter';

function formatWithCurrencyFormatter(amount, options) {
  return currencyFormatterPkg.format(amount, options);
}

export function useCurrencyLocale(
  { useNumberFormat = true } = {
    useNumberFormat: true
  }
) {
  const {
    companyInfo: loggedInContext,
    company: getPrimaryCompany
  } = useContext(AppContext);

  const currency = loggedInContext?.currency || 'USD';

  const fallback = {
    language: 'en',
    country: getPrimaryCompany?.countryCode || 'US',
    currency
  };

  let preset;

  if (useNumberFormat) {
    preset = loggedInContext?.numberFormat ?? fallback;
  } else {
    preset = fallback;
  }

  return {
    language: preset?.language,
    country: preset?.country,
    currency
  };
}

export function useCurrencySymbols(
  { useNumberFormat = true } = { useNumberFormat: true }
) {
  const { language, country, currency } = useCurrencyLocale({
    useNumberFormat
  });

  const locale = `${language}-${country}`;

  let currencySymbol;
  let currencySymbolWithCode;

  try {
    if (INTL_UNSUPPORTED_CURRENCIES.includes(currency)) {
      const formattedCF = formatWithCurrencyFormatter(1, {
        code: currency,
        precision: 0
      });
      if (formattedCF) {
        currencySymbol = formattedCF.replace(/[\d\s.,]/g, '');
        currencySymbolWithCode = `${currencySymbol}${currency}`;
      }
    } else {
      currencySymbol = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        currencyDisplay: 'narrowSymbol'
      })
        .format(0)
        .replace(/\d/g, '')
        .trim();
      currencySymbolWithCode = `${currencySymbol}${currency}`;
    }
  } catch (error) {
    currencySymbol = currency;
    currencySymbolWithCode = currency;
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error(
        `CurrencyField: Intl.NumberFormat failed for locale "${locale}" and currency "${currency}"`,
        error
      );
    }
  }

  return {
    currencySymbolWithCode,
    currencySymbol
  };
}

export function useCurrencyText({
  value,
  minFractionDigits,
  perValue = false,
  denominator = 'hr',
  config = {},
  symbolOnly = false,
}) {
  const { language, country, currency } = useCurrencyLocale();

  const { currencySymbol } = useCurrencySymbols();

  if (symbolOnly) {
    return currencySymbol;
  }

  if (INTL_UNSUPPORTED_CURRENCIES.includes(currency)) {
    return symbolOnly ? currency : formatCurrency(value, currency);
  }

  // const amountStr =
  //   typeof value === 'string' ? value : (value ?? '').toString?.() || '';
  // const lastDot =
  //   amountStr && amountStr.trim() ? amountStr.lastIndexOf('.') : -1;
  // const decimalsCount =
  //   lastDot !== -1
  //     ? (amountStr.slice(lastDot + 1).match(/\d/g) || []).length
  //     : 0;

  let derivedMinimumFractionDigits = 2;
  if (typeof config.minimumFractionDigits === 'number') {
    derivedMinimumFractionDigits = config.minimumFractionDigits;
  } else if (typeof minFractionDigits === 'number') {
    derivedMinimumFractionDigits = minFractionDigits;
  }

  let derivedMaximumFractionDigits = 4;
  if (typeof config.maximumFractionDigits === 'number') {
    derivedMaximumFractionDigits = config.maximumFractionDigits;
  }

  const formatter = new Intl.NumberFormat(`${language}-${country}`, {
    style: 'currency',
    currency,
    minimumFractionDigits: Math.min(2, derivedMinimumFractionDigits),
    maximumFractionDigits: Math.min(4, derivedMaximumFractionDigits),
    currencyDisplay: 'narrowSymbol',
    ...config
  });

  const formattedCurrencyValue = formatter.format(value);
  if (perValue) {
    return `${formattedCurrencyValue}/${denominator}`;
  }
  return formattedCurrencyValue;
}

export const CurrencyText = (props) => {
  return useCurrencyText(props);
};

export function formatCurrencyLocale({
  language = 'en',
  country = 'US',
  currency = 'USD',
  amount = 0,
  minimumFractionDigits,
  maximumFractionDigits = 4
} = {}) {
  if (INTL_UNSUPPORTED_CURRENCIES.includes(currency)) {
    return formatCurrency(amount, currency);
  }

  // const amountStr =
  //   typeof value === 'string' ? value : (value ?? '').toString?.() || '';
  // const lastDot =
  //   amountStr && amountStr.trim() ? amountStr.lastIndexOf('.') : -1;
  // const decimalsCount =
  //   lastDot !== -1
  //     ? (amountStr.slice(lastDot + 1).match(/\d/g) || []).length
  //     : 0;

  let derivedMinimumFractionDigits = 2;
  if (typeof minFractionDigits === 'number') {
    derivedMinimumFractionDigits = minimumFractionDigits;
  }

  let derivedMaximumFractionDigits = 4;
  if (typeof maximumFractionDigits === 'number') {
    derivedMaximumFractionDigits = maximumFractionDigits;
  }

  const formatter = new Intl.NumberFormat(`${language}-${country}`, {
    style: 'currency',
    currency,
    minimumFractionDigits: Math.min(2, derivedMinimumFractionDigits),
    maximumFractionDigits: Math.min(4, derivedMaximumFractionDigits),
    currencyDisplay: 'narrowSymbol'
  });

  const formattedCurrencyValue = formatter.format(amount);

  return formattedCurrencyValue;
}

export function useCurrencyCallback() {
  const { language, country, currency } = useCurrencyLocale();

  return {
    formatCurrency: (value) =>
      formatCurrencyLocale({ amount: value, language, country, currency })
  };
}

export function CurrencyField(props) {
  const { language, country, currency } = useCurrencyLocale();
  return (
    <DesignSystemCurrencyField
      language={language}
      country={country}
      currency={currency}
      unSupportedCurrenciesList={INTL_UNSUPPORTED_CURRENCIES}
      {...props}
    />
  );
}
